import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react"
import { registerSheetContender, sheetContenderCount, subscribeSheetOwner } from "./sheetOwner"

const hasMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
// Layout-phase on the client so the contender registration (and the
// re-render it triggers) completes before the first paint; the server has no
// layout phase and warns on useLayoutEffect.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Should the map-first sheet layout be on right now? (#856)
 *
 * The viewport read is a `useSyncExternalStore` over `matchMedia`: the
 * server snapshot is always false, so SSR HTML and the hydration pass keep
 * the stacked list layout (no mismatch, no desktop shift), while a plain
 * client mount reads the real viewport on its first render. `(max-width: N)`
 * matches width ≤ N, the module's own `innerWidth <= BP` test
 * (explorer-mobile-sheet.js:142-144), and the subscription follows the
 * media query live (rotate across the breakpoint = the module's debounced
 * resize→apply(), js:1708-1712).
 *
 * Also the arm-guard: an eligible instance registers as a contender (layout
 * phase, so the sheet is on before the first paint of a client mount) and
 * is active only while it is the ONLY one in the document (sheetOwner.ts).
 * Either way the results grid keeps its React position across the switch —
 * ExplorerContent renders one tree for both layouts.
 */
export function useMobileResultsActive(enabled: boolean, breakpointPx: number): boolean {
  const query = `(max-width: ${breakpointPx}px)`
  const subscribeMedia = useCallback((onChange: () => void) => {
    if (!hasMatchMedia()) return () => {}
    const mq = window.matchMedia(query)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [query])
  const readMedia = useCallback(() => (hasMatchMedia() ? window.matchMedia(query).matches : false), [query])
  const narrow = useSyncExternalStore(subscribeMedia, readMedia, () => false)

  const want = enabled && narrow

  const tokenRef = useRef<symbol | null>(null)
  if (tokenRef.current === null) tokenRef.current = Symbol('hfse-mres')
  const registeredRef = useRef(false)

  useIsomorphicLayoutEffect(() => {
    if (!want) return
    registeredRef.current = true
    const unregister = registerSheetContender(tokenRef.current!)
    return () => {
      registeredRef.current = false
      unregister()
    }
  }, [want])

  const count = useSyncExternalStore(subscribeSheetOwner, sheetContenderCount, () => 0)

  return want && registeredRef.current && count === 1
}
